import React, { useEffect, useState } from "react";
import { Container, Card, Button, Form, Table, Spinner, Alert } from "react-bootstrap";
import TechnicianService from "../services/technician.service";
import UserService from "../services/user.service";
import type { AccountSettings, CalendarDisplaySettings, Technician } from "./models";

const DEFAULT_CALENDAR_SETTINGS: CalendarDisplaySettings = {
  calendarStartHour: 8,
  calendarEndHour: 18,
};

const hourOptions = Array.from({ length: 24 }, (_, hour) => ({
  value: hour,
  label: `${hour.toString().padStart(2, "0")}:00`,
}));

const Settings = () => {
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [newName, setNewName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isSavingCalendar, setIsSavingCalendar] = useState(false);
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [calendarSuccess, setCalendarSuccess] = useState<string | null>(null);
  const [technicianError, setTechnicianError] = useState<string | null>(null);
  const [technicianSuccess, setTechnicianSuccess] = useState<string | null>(null);
  const [calendarSettings, setCalendarSettings] = useState<CalendarDisplaySettings>(
    DEFAULT_CALENDAR_SETTINGS,
  );

  const fetchTechnicians = async () => {
    setIsLoading(true);
    try {
      const result = await TechnicianService.getTechnicians();
      setTechnicians(result.data.data || []);
    } catch (err) {
      setTechnicianError("Failed to load technicians");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAccountSettings = async () => {
    setIsLoadingCalendar(true);
    try {
      const result = await UserService.getAccountSettings();
      const settings = (result.data.data || {}) as Partial<AccountSettings>;
      setCalendarSettings({
        calendarStartHour:
          settings.calendarStartHour ?? DEFAULT_CALENDAR_SETTINGS.calendarStartHour,
        calendarEndHour:
          settings.calendarEndHour ?? DEFAULT_CALENDAR_SETTINGS.calendarEndHour,
      });
    } catch (err) {
      setCalendarError("Failed to load calendar settings");
    } finally {
      setIsLoadingCalendar(false);
    }
  };

  useEffect(() => {
    fetchTechnicians();
    fetchAccountSettings();
  }, []);

  const handleAdd = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setIsAdding(true);
    setTechnicianError(null);
    setTechnicianSuccess(null);
    try {
      await TechnicianService.createTechnician({ name: newName.trim() });
      setNewName("");
      setTechnicianSuccess("Technician added successfully");
      await fetchTechnicians();
      setTimeout(() => setTechnicianSuccess(null), 3000);
    } catch (err) {
      setTechnicianError("Failed to add technician");
    } finally {
      setIsAdding(false);
    }
  };

  const handleCalendarSettingsChange = (
    field: keyof CalendarDisplaySettings,
    value: string,
  ) => {
    setCalendarSettings((currentSettings) => ({
      ...currentSettings,
      [field]: parseInt(value, 10),
    }));
  };

  const handleCalendarSettingsSave = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    setIsSavingCalendar(true);
    setCalendarError(null);
    setCalendarSuccess(null);

    try {
      const response = await UserService.updateAccountSettings(calendarSettings);
      setCalendarSettings({
        calendarStartHour: response.data.data.calendarStartHour,
        calendarEndHour: response.data.data.calendarEndHour,
      });
      setCalendarSuccess("Calendar display hours saved");
      setTimeout(() => setCalendarSuccess(null), 3000);
    } catch (err) {
      setCalendarError("Failed to save calendar settings");
    } finally {
      setIsSavingCalendar(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this technician?")) return;
    setTechnicianError(null);
    setTechnicianSuccess(null);
    try {
      await TechnicianService.deleteTechnician(id);
      setTechnicianSuccess("Technician deleted");
      await fetchTechnicians();
      setTimeout(() => setTechnicianSuccess(null), 3000);
    } catch (err) {
      setTechnicianError("Failed to delete technician");
    }
  };

  return (
    <Container className="mt-4">
      <h2 className="mb-4">Settings</h2>

      <Card className="shadow-sm mb-4">
        <Card.Header as="h5" className="bg-dark text-white">
          Calendar Display Hours
        </Card.Header>
        <Card.Body>
          {calendarError && (
            <Alert variant="danger" onClose={() => setCalendarError(null)} dismissible>
              {calendarError}
            </Alert>
          )}
          {calendarSuccess && (
            <Alert variant="success" onClose={() => setCalendarSuccess(null)} dismissible>
              {calendarSuccess}
            </Alert>
          )}
          <Form onSubmit={handleCalendarSettingsSave}>
            <div className="d-flex flex-wrap gap-3 align-items-end">
              <Form.Group>
                <Form.Label>Show calendar from</Form.Label>
                <Form.Select
                  value={calendarSettings.calendarStartHour}
                  disabled={isSavingCalendar || isLoadingCalendar}
                  onChange={(e) =>
                    handleCalendarSettingsChange("calendarStartHour", e.target.value)
                  }
                >
                  {hourOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Show calendar until</Form.Label>
                <Form.Select
                  value={calendarSettings.calendarEndHour}
                  disabled={isSavingCalendar || isLoadingCalendar}
                  onChange={(e) =>
                    handleCalendarSettingsChange("calendarEndHour", e.target.value)
                  }
                >
                  {hourOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Button type="submit" variant="primary" disabled={isSavingCalendar || isLoadingCalendar}>
                {isSavingCalendar ? <Spinner animation="border" size="sm" /> : "Save hours"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header as="h5" className="bg-dark text-white">
          Technicians
        </Card.Header>
        <Card.Body>
          {technicianError && (
            <Alert variant="danger" onClose={() => setTechnicianError(null)} dismissible>
              {technicianError}
            </Alert>
          )}
          {technicianSuccess && (
            <Alert variant="success" onClose={() => setTechnicianSuccess(null)} dismissible>
              {technicianSuccess}
            </Alert>
          )}

          <Form onSubmit={handleAdd} className="mb-4">
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Technician name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                disabled={isAdding}
                style={{ maxWidth: 300 }}
              />
              <Button type="submit" variant="primary" disabled={isAdding || !newName.trim()}>
                {isAdding ? <Spinner animation="border" size="sm" /> : "+ Add"}
              </Button>
            </div>
          </Form>

          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : technicians.length === 0 ? (
            <p className="text-muted">No technicians added yet.</p>
          ) : (
            <Table striped bordered hover responsive>
              <thead className="table-dark">
                <tr>
                  <th>Name</th>
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {technicians.map((tech) => (
                  <tr key={tech.id}>
                    <td>{tech.name}</td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(tech.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Settings;
